import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Icon } from '../../ui/icons';
import './comments.css';

/** Safe JSON.parse for the activity metadata payloads written by the comment handlers. */
function parseJson(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function commentMentions(activity) {
  return parseJson(activity?.metadata?.mentions) || [];
}

export function commentAttachments(activity) {
  return parseJson(activity?.metadata?.attachments) || [];
}

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Comment body with "@Name" tokens highlighted for the names actually mentioned. */
export function CommentText({ text, mentions }) {
  if (!text) return null;
  const names = (mentions || []).map((m) => m.name).filter(Boolean);
  if (names.length === 0) return <>{text}</>;
  const pattern = new RegExp(`(@(?:${names.map(escapeRegex).join('|')}))`, 'g');
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('@') && names.includes(part.slice(1))
          ? <span key={i} className="pp-comment-mention">{part}</span>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

/** Image attachments render as inline thumbnails via a short-lived presigned URL. */
function CommentImage({ attachmentsBasePath, file }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`${attachmentsBasePath}/${file.id}/download-url`)
      .then((res) => { if (alive && res?.url) setUrl(res.url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [attachmentsBasePath, file.id]);

  if (!url) {
    return (
      <span className="pp-comment-file pp-comment-file--loading">
        <Icon.Image size={13} /> {file.name}
      </span>
    );
  }
  return (
    <a className="pp-comment-thumb" href={url} target="_blank" rel="noopener noreferrer" title={file.name}>
      <img src={url} alt={file.name} loading="lazy" />
    </a>
  );
}

function CommentFileChip({ attachmentsBasePath, file }) {
  const open = async () => {
    try {
      const res = await api.get(`${attachmentsBasePath}/${file.id}/download-url`);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch { /* advisory */ }
  };
  return (
    <button type="button" className="pp-comment-file" onClick={open} title={file.name}>
      <Icon.FileText size={13} />
      <span className="pp-comment-file__name">{file.name}</span>
      <Icon.Download size={12} />
    </button>
  );
}

/**
 * Attachment strip under a comment: image thumbnails first, then file chips.
 *
 * Props:
 *   activity: the Commented activity entry (metadata.attachments JSON)
 *   attachmentsBasePath: e.g. `/requisitions/{id}/attachments` — used for presigned download URLs
 */
export function CommentAttachments({ activity, attachmentsBasePath }) {
  const files = commentAttachments(activity);
  if (files.length === 0) return null;
  const images = files.filter((f) => f.contentType?.startsWith('image/'));
  const others = files.filter((f) => !f.contentType?.startsWith('image/'));
  return (
    <div className="pp-comment-attachments">
      {images.map((f) => <CommentImage key={f.id} attachmentsBasePath={attachmentsBasePath} file={f} />)}
      {others.map((f) => <CommentFileChip key={f.id} attachmentsBasePath={attachmentsBasePath} file={f} />)}
    </div>
  );
}
